/*!
 * ==============================================================
 * analytics.js
 * GA4 Tracking Library for Shopify (Concept Theme)
 * ==============================================================
 */

(function () {
    'use strict';

    if (window.AnalyticsTracker) return;

    class AnalyticsTracker {

        constructor() {
            this.version = '1.0.0';
            this.debug = false;
            this.pageType = this.detectPageType();
            this.productName = this.getProductName();
            this.lastEvents = new Map();
            this.initialized = false;

            window.dataLayer = window.dataLayer || [];

            this.init();
        }

        /*--------------------------------------------------
        Init
        --------------------------------------------------*/
        init() {
            if (this.initialized) return;
            this.initialized = true;

            this.refreshPageData();
            this.bindThemeEvents();
            this.registerProductEvents();
            this.registerNavigationEvents();
            this.registerIntegrations();
            this.registerEcommerceEvents();
            this.bindDynamicSections();
            this.startObserver();

            this.log('Analytics Ready');
        }

        /*--------------------------------------------------
        Debug
        --------------------------------------------------*/
        log(...args) {
            if (!this.debug) return;
            console.log('[Analytics]', ...args);
        }

        /*--------------------------------------------------
        Page Detection
        --------------------------------------------------*/
        detectPageType() {
            const path = window.location.pathname;

            if (path === '/' || document.body.classList.contains('template-index')) {
                return 'homepage';
            }
            if (path.indexOf('/products/') > -1) {
                return 'product_page';
            }
            if (path.indexOf('/collections/') > -1) {
                return 'collection_page';
            }
            if (path.indexOf('/blogs/') > -1) {
                return 'blog_page';
            }
            if (path.indexOf('/pages/') > -1) {
                return 'page';
            }
            if (path.indexOf('/cart') > -1) {
                return 'cart';
            }
            return 'other';
        }

        refreshPageData() {
            this.pageType = this.detectPageType();
            this.productName = this.getProductName();
        }

        /*--------------------------------------------------
        Product
        --------------------------------------------------*/
        getProductName() {
            // Only product pages have a real product to report. On other
            // page types (homepage, collections, etc.) report 'NA' instead
            // of falling back to an unrelated on-page h1.
            if (this.pageType !== 'product_page') {
                return 'NA';
            }

            if (window.ShopifyAnalytics &&
                window.ShopifyAnalytics.meta &&
                window.ShopifyAnalytics.meta.product &&
                window.ShopifyAnalytics.meta.product.title) {

                return window.ShopifyAnalytics.meta.product.title;
            }

            if (window.meta && window.meta.product && window.meta.product.title) {
                return window.meta.product.title;
            }

            const selectors = [
                'h1.product__title',
                '.product__title h1',
                '.product-single__title',
                '.product__title',
                '.product-title',
                'h1'
            ];

            for (const selector of selectors) {
                const el = document.querySelector(selector);
                if (!el) continue;

                // Clone so we don't touch the live DOM, then strip out
                // nested price/badge/hidden markup that pollutes the title text
                const clone = el.cloneNode(true);
                clone.querySelectorAll(
                    '.price, .badge, .visually-hidden, [class*="price"], script, style'
                ).forEach(n => n.remove());

                const cleaned = clone.textContent.replace(/\s+/g, ' ').trim();
                if (cleaned) return cleaned;
            }
            return '';
        }

        /*--------------------------------------------------
        Duplicate Protection
        --------------------------------------------------*/
        canFire(event, value = '') {
            const key = event + '_' + value;
            const now = Date.now();

            if (!this.lastEvents.has(key)) {
                this.lastEvents.set(key, now);
                return true;
            }

            const last = this.lastEvents.get(key);
            if ((now - last) < 800) {
                return false;
            }

            this.lastEvents.set(key, now);
            return true;
        }

        /*--------------------------------------------------
        Push Event
        --------------------------------------------------*/
        push(eventName, payload = {}) {
            const data = {
                event: eventName,
                page_type: this.pageType,
                product_name: this.productName,
                ...payload
            };

            window.dataLayer.push(data);
            this.log(data);
        }

        /*--------------------------------------------------
        Helpers
        --------------------------------------------------*/
        text(el) {
            if (!el) return '';

            // Some summaries/links render a visually-hidden duplicate label
            // alongside the visible one (e.g. for accessibility), which
            // textContent would otherwise pick up twice. Strip those first.
            const clone = el.cloneNode(true);
            clone.querySelectorAll('.sr-only, .visually-hidden').forEach(n => n.remove());

            let cleaned = clone.textContent.replace(/\s+/g, ' ').trim();

            // Some nav items use a CSS hover-reveal trick with two fully
            // visible, identical text elements stacked on top of each other
            // (no hiding class/attribute to strip) — e.g. "Protective" +
            // "Protective" becomes "ProtectiveProtective". If the whole
            // string is just the same substring repeated twice, collapse it.
            if (cleaned.length % 2 === 0) {
                const half = cleaned.length / 2;
                if (cleaned.slice(0, half) === cleaned.slice(half)) {
                    cleaned = cleaned.slice(0, half);
                }
            }

            return cleaned;
        }

        closestText(element, selector) {
            const node = element.closest(selector);
            return node ? this.text(node) : '';
        }

        /*--------------------------------------------------
        Theme Lifecycle Events
        --------------------------------------------------*/
        bindThemeEvents() {
            document.addEventListener('page:loaded', () => {
                this.refreshPageData();
                this.log('page:loaded');
            });

            document.addEventListener('shopify:section:load', () => {
                this.refreshPageData();
                this.log('section loaded');
            });

            document.addEventListener('shopify:section:reorder', () => {
                this.refreshPageData();
            });
        }

        /*====================================================
        PRODUCT PAGE EVENTS
        ====================================================*/
        registerProductEvents() {
            this.variantSelection();
            this.dropdownInteraction();
            this.shareAndHelp();
            this.sizeGuideInteraction();
        }

        variantSelection() {
            document.addEventListener('change', (e) => {

                // Ignore synthetic events dispatched by third-party apps
                // (e.g. GPO syncing its hidden mirror inputs) — only react
                // to genuine user interaction.
                if (!e.isTrusted) return;

                // Case 1: Native Shopify variant-picker (color/size swatches, dropdowns)
                const nativeInput = e.target.closest(
                    'variant-picker input[type="radio"], variant-picker select'
                );

                if (nativeInput) {
                    this.trackNativeVariant(nativeInput);
                    return;
                }

                // Case 2: Third-party options app (GPO) — freight, Match Ready,
                // Double Grip, Weight, etc.
                const gpoInput = e.target.closest(
                    '.gpo-app input[data-field-name], .gpo-app select[data-field-name]'
                );

                if (gpoInput) {
                    // Prefer the VISIBLE on-page label (.gpo-label .label-content)
                    // over the internal admin field name (data-field-name), since
                    // these can differ (e.g. field named "Free Knocking" but
                    // displayed on-page as "15000 Knocks").
                    const groupEl = gpoInput.closest('.gpo-element, .gpo-form__group');
                    const visibleLabel = groupEl
                        ? groupEl.querySelector(':scope > label .label-content, .gpo-label .label-content')
                        : null;

                    const optionName = (
                        (visibleLabel && this.text(visibleLabel)) ||
                        gpoInput.dataset.fieldName ||
                        gpoInput.name ||
                        'Variant'
                    ).trim();

                    const selectedValue = (gpoInput.value || '').trim();

                    if (!this.canFire('variant_selection', optionName + selectedValue)) return;

                    this.push('variant_selection', {
                        selection_type: optionName,
                        selected_value: selectedValue
                    });
                }
            });

        }

        trackNativeVariant(input) {
            const wrapper = input.closest('[data-option-slug]');
            let optionName = '';

            if (wrapper) {
                optionName = wrapper.dataset.optionSlug
                    .replace(/-/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());
            } else {
                optionName = input.name || 'Variant';
            }

            const selectedValue = input.value || '';

            if (!this.canFire('variant_selection', optionName + selectedValue)) return;

            this.push('variant_selection', {
                selection_type: optionName,
                selected_value: selectedValue
            });
        }

        dropdownInteraction() {
            // 'toggle' fires on the <details> element itself after its open
            // state changes — more reliable than reading state from a click,
            // and lets us report action: 'expand' / 'collapse'.
            // Note: 'toggle' does not bubble, so we must listen in the
            // capture phase to catch it via delegation.
            document.addEventListener('toggle', (e) => {
                const details = e.target;
                if (!(details instanceof HTMLDetailsElement)) return;

                // Skip — this is a nav/menu-drawer dropdown, not a product
                // information accordion. navigationInteraction() handles those.
                if (details.closest('header, nav, .menu-drawer, .list-menu, [data-menu], .header__menu, menu-drawer')) {
                    return;
                }

                const summary = details.querySelector(':scope > summary');
                const text = summary ? this.text(summary) : '';
                if (!text) return;

                const action = details.open ? 'expand' : 'collapse';

                if (!this.canFire('dropdown_interaction', text + action)) return;

                this.push('dropdown_interaction', {
                    link_text: text,
                    action: action
                });
            }, true);
        }

        shareAndHelp() {
            document.addEventListener('click', (e) => {
                const btn = e.target.closest('button,a');
                if (!btn) return;

                const visibleText = this.text(btn);
                const ariaLabel = btn.getAttribute('aria-label') || '';

                // Match against both — icon-only share buttons (e.g. desktop
                // Facebook/Twitter icons) have no visible text at all, only
                // an aria-label like "Share on Facebook".
                if (!/share|need help/i.test(`${visibleText} ${ariaLabel}`)) return;

                const label = visibleText || ariaLabel;
                if (!label) return;

                if (!this.canFire('link_click', label)) return;

                this.push('link_click', {
                    link_text: label
                });
            });
        }

        sizeGuideInteraction() {
            document.addEventListener('click', (e) => {
                const btn = e.target.closest(
                    'button[aria-controls^="SizeChart-"], [data-sg-open]'
                );
                if (!btn) return;

                if (!this.canFire('size_guide')) return;

                this.push('size_guide_interaction');
            });
        }

        /*====================================================
        NAVIGATION / COLLECTION EVENTS
        ====================================================*/
        registerNavigationEvents() {
            this.galleryInteraction();
            this.videoInteraction();
            this.productCategoryInteraction();
            this.navigationInteraction();
        }

        galleryInteraction() {
            document.addEventListener('click', (e) => {
                const media = e.target.closest('[data-media-id]');
                if (!media) return;

                const mediaId = media.dataset.mediaId || '';
                // Shared key with the touchend handler below — on touch
                // devices both click and touchend fire for the same tap,
                // so they must dedupe against each other, not separately.
                if (!this.canFire('gallery_interaction', mediaId)) return;

                this.push('gallery_interaction');
            });

            document.addEventListener('touchend', (e) => {
                const media = e.target.closest('[data-media-id]');
                if (!media) return;

                const mediaId = media.dataset.mediaId || '';
                if (!this.canFire('gallery_interaction', mediaId)) return;

                this.push('gallery_interaction');
            }, { passive: true });
        }

        videoInteraction() {
            // Videos with an explicit poster/play-pause button (e.g. hero
            // banner videos) — track the deliberate button click itself.
            // DeferredMedia.onPosterClick() reads the CURRENT 'playing'
            // state before toggling it, so checking that same attribute
            // at click-time tells us exactly which action is about to
            // happen — no guessing, no waiting for the async toggle.
            //
            // IMPORTANT: onPosterClick() calls event.stopPropagation(),
            // which would block a normal bubble-phase document listener
            // from ever seeing this click. Using the capture phase (true)
            // means our listener runs BEFORE the button's own handler,
            // so its later stopPropagation() call has no effect on us.
            document.addEventListener('click', (e) => {
                const posterBtn = e.target.closest('.deferred-poster, [id^="DeferredPoster-"]');
                if (!posterBtn) return;

                const wrapper = posterBtn.closest('video-media, model-media, .deferred-media');
                if (!wrapper) return;

                const wasPlaying = wrapper.hasAttribute('playing');
                const action = wasPlaying ? 'pause' : 'play';

                if (!this.canFire('video_button_' + action, posterBtn.id || '')) return;

                this.push('video_interaction', {
                    action: action
                });
            }, true);

            // Videos with NO button at all (pure background autoplay
            // loops) — fall back to native play/pause events, logged once
            // per action per video for the whole page session, since
            // autoplay re-fires the same native event every time the
            // video scrolls back into view.
            const loggedActions = new WeakMap();

            const track = (action) => (e) => {
                const video = e.target;
                if (!(video instanceof HTMLVideoElement)) return;

                // Skip videos that have their own poster/button — those
                // are tracked precisely via the click listener above instead.
                const wrapper = video.closest('video-media, model-media, .deferred-media');
                if (wrapper && wrapper.querySelector('.deferred-poster, [id^="DeferredPoster-"]')) return;

                let logged = loggedActions.get(video);
                if (!logged) {
                    logged = new Set();
                    loggedActions.set(video, logged);
                }
                if (logged.has(action)) return;
                logged.add(action);

                this.push('video_interaction', {
                    action: action
                });
            };

            document.addEventListener('play', track('play'), true);
            document.addEventListener('pause', track('pause'), true);
        }

        productCategoryInteraction() {
            // Only fire within these specific collection-carousel sections
            // — not any arbitrary /collections/ link anywhere on the page.
            // NOTE: these are the current section IDs; if this carousel
            // block gets duplicated or added to a new page, its new
            // section ID will need adding here too.
            const allowedSectionIds = [
                'shopify-section-template--20119670554666__collection_list_qcHpkA',
                'shopify-section-template--20119670882346__collection-list',
                'shopify-section-template--20119670882346__collection_list_ixQRgD',
                'shopify-section-template--20119671570474__collection_list_zrLkpf'
            ];

            document.addEventListener('click', (e) => {
                const link = e.target.closest('a[href*="/collections/"]');
                if (!link) return;

                const section = link.closest('[id^="shopify-section-"]');
                if (!section || !allowedSectionIds.includes(section.id)) return;

                const text = this.text(link);
                if (!text) return;

                if (!this.canFire('product_category', text)) return;

                this.push('product_category_interaction', {
                    link_text: text
                });
            });
        }

        navigationInteraction() {
            document.addEventListener('click', (e) => {
                const link = e.target.closest(
                    '.list-menu a, .menu__item, .drawer__menu-item, summary, a'
                );
                if (!link) return;

                // Skip the cart icon/link — it's not a real navigation menu
                // item, even though it may sit inside the header markup.
                const href = link.getAttribute('href') || '';
                const ariaControls = link.getAttribute('aria-controls') || '';
                if (/\/cart(\/|$|\?)/.test(href) || /cart/i.test(ariaControls)) {
                    return;
                }

                const navContainer = link.closest(
                    'header, nav, .menu-drawer, .list-menu, [data-menu], .header__menu, menu-drawer'
                );
                if (!navContainer) return;

                const linkText = this.text(link);
                if (!linkText) return;

                // Walk up through ancestor <li> elements to build the
                // category path, from outermost to innermost, skipping the
                // clicked item's own label.
                const path = [];
                let li = link.closest('li');

                while (li) {
                    const label = li.querySelector(':scope > a, :scope > details > summary, :scope > summary');
                    if (label) {
                        const labelText = this.text(label);
                        if (labelText && labelText !== linkText && !path.includes(labelText)) {
                            path.unshift(labelText);
                        }
                    }
                    const parentLi = li.parentElement ? li.parentElement.closest('li') : null;
                    li = parentLi;
                }

                const fullPath = [...path, linkText];
                const category = fullPath[0];
                const subCategory = fullPath.length >= 2 ? fullPath[1] : 'NA';

                // Only apply the "NA for non-navigable clicks" rule inside
                // the mobile drawer, where category/sub-category items are
                // <summary> toggles with no real href. On desktop, the
                // equivalent items are genuine <a href> links, so their own
                // label should always be reported normally.
                const isInDrawer = !!link.closest('menu-drawer, .menu-drawer');
                const isRealLink = link.tagName === 'A' && link.hasAttribute('href');
                const linkTextValue = (isInDrawer && !isRealLink) ? 'NA' : linkText;

                if (!this.canFire('navigation', category + subCategory + linkTextValue)) return;

                this.push('navigation_interaction', {
                    category_text: category,
                    sub_category_text: subCategory,
                    link_text: linkTextValue
                });
            });
        }

        /*====================================================
        GA4 ECOMMERCE EVENTS
        ====================================================*/
        registerEcommerceEvents() {
            this.viewItemTracking();
            this.addToCartTracking();
        }

        /*--------------------------------------------------
        view_item
        --------------------------------------------------*/
        viewItemTracking() {
            // ProductInfo dispatches this bubbling event once when it
            // connects — on the initial page load, and again on any AJAX
            // re-render (e.g. Shopify section reload). Scoped to real
            // product pages only.
            document.addEventListener('product-info:loaded', (e) => {
                if (this.pageType !== 'product_page') return;

                const productInfo = e.target;
                if (!(productInfo instanceof HTMLElement)) return;
                if (productInfo.tagName !== 'PRODUCT-INFO') return;

                const variant = this.getVariantData(productInfo);
                if (!variant) return;

                if (!this.canFire('view_item', variant.id)) return;

                this.pushEcommerce('view_item', variant, 1);
            });
        }

        /*--------------------------------------------------
        add_to_cart
        --------------------------------------------------*/
        addToCartTracking() {
            // Dispatched by ProductForm / ProductBundle in theme.js right
            // after a successful /cart/add.js call, carrying Shopify's
            // Ajax Cart API response with real price/variant/quantity data.
            document.addEventListener('ajaxProduct:added', (e) => {
                const product = e.detail && e.detail.product;
                if (!product) return;

                // Single-item add returns the item directly; bundle/
                // multi-item add returns { items: [...] }.
                const items = Array.isArray(product.items) ? product.items : [product];
                if (!items.length) return;

                const mappedItems = items.map((item) => ({
                    item_id: item.sku || String(item.variant_id || item.id || ''),
                    item_name: item.product_title || item.title || '',
                    item_variant: item.variant_title || '',
                    price: (item.price || 0) / 100,
                    quantity: item.quantity || 1
                }));

                const dedupeKey = mappedItems.map((i) => i.item_id + i.quantity).join('|');
                if (!this.canFire('add_to_cart', dedupeKey)) return;

                const value = mappedItems.reduce((sum, i) => sum + (i.price * i.quantity), 0);

                window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce object per GA4 recommendation
                this.push('add_to_cart', {
                    ecommerce: {
                        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '',
                        value: value,
                        items: mappedItems
                    }
                });
            });
        }

        /*--------------------------------------------------
        Shared ecommerce helpers
        --------------------------------------------------*/
        getVariantData(productInfoEl) {
            const script = productInfoEl.querySelector('[data-selected-variant]');
            if (!script || !script.textContent) return null;

            try {
                return JSON.parse(script.textContent);
            }
            catch (e) {
                return null;
            }
        }

        pushEcommerce(eventName, variant, quantity) {
            const price = (variant.price || 0) / 100;

            window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce object per GA4 recommendation
            this.push(eventName, {
                ecommerce: {
                    currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '',
                    value: price * quantity,
                    items: [{
                        item_id: variant.sku || String(variant.id || ''),
                        item_name: this.productName,
                        item_variant: variant.public_title || variant.title || '',
                        price: price,
                        quantity: quantity
                    }]
                }
            });
        }

        /*====================================================
        THIRD-PARTY / THEME INTEGRATIONS
        ====================================================*/
        registerIntegrations() {
            this.chatraTracking();
            this.reviewTracking();
            this.themePubSub();
        }

        chatraTracking() {
            window.ChatraSetup = window.ChatraSetup || {};
            const self = this;

            window.ChatraSetup.onAnalyticEvent = function (eventName) {
                const allowedEvents = [
                    'Chat initiated by visitor',
                    'Bot scenario started by visitor',
                    'Targeted chat shown'
                ];

                if (!allowedEvents.includes(eventName)) return;
                if (!self.canFire('chatra', eventName)) return;

                self.push('chat_bot_interaction');
            };
        }

        reviewTracking() {
            document.addEventListener('click', (e) => {
                const btn = e.target.closest('.reputon-leave-review, .reputon-link');
                if (!btn) return;

                if (!this.canFire('review')) return;

                this.push('review_interaction');
            });
        }

        themePubSub() {
            if (!window.theme) return;
            if (!theme.pubsub) return;
            if (!theme.pubsub.subscribe) return;

            const self = this;

            // NOTE: We deliberately do NOT push a 'variant_selection' event
            // here. The theme's own pubsub fires 'variantChange' and
            // 'optionValueSelectionChange' for the same interaction that
            // variantSelection()'s native 'change' listener already
            // captures — with better data (real option name + selected
            // value). Subscribing here too caused every variant click to
            // fire 3 duplicate events. We still refresh cached page data
            // on these signals since that part is harmless and useful.
            theme.pubsub.subscribe('variantChange', function (event) {
                if (!event) return;
                self.refreshPageData();
            });

            theme.pubsub.subscribe('optionValueSelectionChange', function (event) {
                if (!event) return;
                self.refreshPageData();
            });
        }

        /*--------------------------------------------------
        Refresh + Rebind on Shopify Section Rendering
        --------------------------------------------------*/
        refresh() {
            this.refreshPageData();
        }

        bindDynamicSections() {
            document.addEventListener('shopify:section:load', () => {
                this.refresh();
            });
            document.addEventListener('shopify:section:select', () => {
                this.refresh();
            });
            document.addEventListener('shopify:section:reorder', () => {
                this.refresh();
            });
        }

        /*--------------------------------------------------
        Observe Dynamic DOM (AJAX cart drawers, quick view, etc.)
        --------------------------------------------------*/
        startObserver() {
            const observer = new MutationObserver(() => {
                this.refresh();
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    window.AnalyticsTracker = new AnalyticsTracker();

})();